import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { 
  Check, 
  ArrowRight, 
  Crown,
  Users,
  Star,
  Loader2,
  CreditCard
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const tierIcons = { par: Users, birdie: Star, eagle: Crown };
const tierAccents = {
  par: 'border-gray-200',
  birdie: 'border-gold ring-2 ring-gold/20',
  eagle: 'border-deep-green ring-2 ring-deep-green/20',
};

const MembershipPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [currentSubscription, setCurrentSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingTier, setProcessingTier] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch tiers
        const tiersRes = await axios.get(`${API_URL}/payments/tiers`);
        setTiers(tiersRes.data.tiers || []);

        // Fetch current subscription if logged in
        if (user) {
          try {
            const token = await user.getIdToken();
            const subRes = await axios.get(`${API_URL}/payments/my-subscription`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            setCurrentSubscription(subRes.data);
          } catch (err) {
            console.log('No subscription found');
          }
        }
      } catch (err) {
        console.error('Failed to fetch data:', err);
      } finally {
        setLoading(false);
      }
    };
    
    if (!authLoading) {
      fetchData();
    }
  }, [user, authLoading]);

  const handleSubscribe = async (tierId) => {
    if (!user) {
      navigate('/register');
      return;
    }

    if (tierId === 'free') {
      return; // Free tier doesn't need payment
    }

    setProcessingTier(tierId);

    try {
      const token = await user.getIdToken();
      const originUrl = window.location.origin;
      
      const response = await axios.post(
        `${API_URL}/payments/checkout`,
        {
          tier_id: tierId,
          origin_url: originUrl
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Redirect to Stripe Checkout
      if (response.data.url) {
        window.location.href = response.data.url;
      }
    } catch (err) {
      console.error('Payment error:', err);
      alert('Failed to initiate payment. Please try again.');
    } finally {
      setProcessingTier(null);
    }
  };

  const isCurrentTier = (tierId) => {
    if (!currentSubscription) return tierId === 'par';
    return currentSubscription.membership_tier === tierId || 
           (tierId === 'eagle' && currentSubscription.membership_tier === 'eagle');
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-bone pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-fairway" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-24 pb-20">
      {/* Header */}
      <div className="bg-deep-green py-16 mb-12">
        <div className="container-padding text-center">
          <p className="text-gold font-semibold tracking-widest uppercase text-sm mb-3">Membership</p>
          <h1 className="font-display font-bold text-3xl md:text-4xl lg:text-5xl text-white mb-4" data-testid="membership-title">
            Choose Your Level
          </h1>
          <p className="text-cream/70 font-body max-w-2xl mx-auto text-lg">
            From exploring the directory to leading the movement — find the tier that matches your game.
          </p>
        </div>
      </div>

      <div className="container-padding">
        {/* Current Status Banner */}
        {user && currentSubscription && (
          <div className="max-w-5xl mx-auto mb-8">
            <div className="bg-white border border-gold/30 rounded-sm p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gold/20 rounded-full flex items-center justify-center">
                  {currentSubscription.membership_tier === 'eagle' ? (
                    <Crown className="w-5 h-5 text-gold" />
                  ) : currentSubscription.membership_tier === 'birdie' ? (
                    <Star className="w-5 h-5 text-gold" />
                  ) : (
                    <Users className="w-5 h-5 text-gray-500" />
                  )}
                </div>
                <div>
                  <p className="font-semibold text-deep-green">
                    Current Plan: {currentSubscription.membership_tier === 'eagle' ? 'Eagle' : 
                                   currentSubscription.membership_tier === 'birdie' ? 'Birdie' : 'Par (Free)'}
                  </p>
                  {currentSubscription.membership_started_at && (
                    <p className="text-sm text-muted-foreground">
                      Member since {new Date(currentSubscription.membership_started_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tiers Grid */}
        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {tiers.map((tier) => {
            const Icon = tierIcons[tier.id] || Users;
            const accent = tierAccents[tier.id] || '';
            const isPopular = tier.id === 'birdie';
            const isCurrent = isCurrentTier(tier.id);
            const isProcessing = processingTier === tier.id;

            return (
              <Card 
                key={tier.id} 
                className={`bg-white border-2 ${accent} ${isCurrent ? 'ring-2 ring-gold' : ''} relative overflow-hidden transition-all hover:shadow-lg`}
                data-testid={`tier-card-${tier.id}`}
              >
                {isPopular && (
                  <div className="absolute top-0 left-0 right-0 bg-gold text-deep-green text-center text-xs font-bold py-1.5 uppercase tracking-wider">
                    Most Popular
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute top-0 right-0 bg-fairway text-white text-xs font-bold px-3 py-1 rounded-bl">
                    Current
                  </div>
                )}
                <CardContent className={`p-8 ${isPopular ? 'pt-12' : ''}`}>
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mb-5 ${
                    tier.id === 'eagle' ? 'bg-deep-green' : tier.id === 'birdie' ? 'bg-gold/20' : 'bg-gray-100'
                  }`}>
                    <Icon className={`w-7 h-7 ${
                      tier.id === 'eagle' ? 'text-gold' : tier.id === 'birdie' ? 'text-gold' : 'text-gray-500'
                    }`} />
                  </div>

                  {/* Name */}
                  <h3 className="font-display font-bold text-xl text-deep-green mb-1">{tier.name}</h3>

                  {/* Price */}
                  <div className="mb-6">
                    {tier.price === 0 ? (
                      <div>
                        <span className="font-display font-bold text-4xl text-deep-green">Free</span>
                      </div>
                    ) : (
                      <div>
                        <span className="font-display font-bold text-4xl text-deep-green">${tier.price}</span>
                        <span className="text-muted-foreground font-body">/{tier.interval}</span>
                      </div>
                    )}
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {tier.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Check className={`w-4 h-4 mt-0.5 shrink-0 ${
                          tier.id === 'eagle' ? 'text-deep-green' : 'text-gold'
                        }`} />
                        <span className="text-sm text-foreground font-body">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <Button 
                    className={`w-full ${
                      isCurrent ? 'bg-gray-200 text-gray-500 cursor-default' :
                      isPopular ? 'btn-gold' : tier.id === 'eagle' 
                        ? 'bg-deep-green hover:bg-deep-green/90 text-white' 
                        : 'border-fairway text-fairway hover:bg-fairway hover:text-white'
                    }`}
                    variant={tier.id === 'par' ? 'outline' : 'default'}
                    disabled={isCurrent || isProcessing}
                    onClick={() => handleSubscribe(tier.id)}
                    data-testid={`tier-cta-${tier.id}`}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Processing...
                      </>
                    ) : isCurrent ? (
                      'Current Plan'
                    ) : tier.price === 0 ? (
                      <>
                        Get Started Free
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe Now
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-2xl mx-auto text-center">
          <h3 className="font-display font-bold text-2xl text-deep-green mb-6">Frequently Asked</h3>
          <div className="space-y-4 text-left">
            {[
              { q: "Can I upgrade anytime?", a: "Yes. Upgrade from Par to Birdie or Eagle at any time. Your benefits activate immediately." },
              { q: "What payment methods do you accept?", a: "We accept all major credit cards through Stripe's secure payment system." },
              { q: "Is there a refund policy?", a: "Yes. If you're not satisfied, contact us within 30 days for a full refund." },
              { q: "Can I cancel my subscription?", a: "You can cancel anytime. Your benefits will remain active until the end of your billing period." },
            ].map((faq, i) => (
              <div key={i} className="bg-white p-5 rounded-sm shadow-sm">
                <h4 className="font-display font-semibold text-deep-green mb-1">{faq.q}</h4>
                <p className="text-muted-foreground text-sm font-body">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MembershipPage;
